import { useEffect, useMemo, useState, type ReactNode } from 'react'
import type { List, Ticket } from '@/lib/types'
import { cn, displayListName, formatDate, groupMedia, labelColor, PRIORITY_META } from '@/lib/utils'
import { htmlToText, sanitizeRichHtml } from '@/lib/richtext'
import { useConfirm } from './ConfirmDialog'
import {
  X,
  ArchiveRestore,
  Trash2,
  Inbox,
  Search,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Image as ImageIcon,
  Video as VideoIcon,
  Mic,
  FileText,
  Paperclip,
} from 'lucide-react'

interface Props {
  open: boolean
  projectName: string
  tickets: Ticket[] // archived tickets of the current project
  lists: List[]
  /** Global search query — pre-fills the archives filter when the modal opens. */
  searchQuery: string
  onClose: () => void
  onRestore: (id: string) => void | Promise<void>
  onDelete: (id: string) => void | Promise<void>
  /** Open the fullscreen media viewer with the given URLs. */
  onViewMedia: (urls: string[]) => void
}

export function ArchivedCardsModal({
  open,
  projectName,
  tickets,
  lists,
  searchQuery,
  onClose,
  onRestore,
  onDelete,
  onViewMedia,
}: Props) {
  const [localQuery, setLocalQuery] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const confirm = useConfirm()

  // When the modal opens, inherit the board's active search so the user
  // immediately sees which archived cards match.
  useEffect(() => {
    if (open) {
      setLocalQuery(searchQuery)
      setExpandedId(null)
    }
  }, [open, searchQuery])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const filtered = useMemo(() => {
    const q = localQuery.trim().toLowerCase()
    if (!q) return tickets
    return tickets.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        htmlToText(t.description ?? '').toLowerCase().includes(q) ||
        t.labels.some((l) => l.toLowerCase().includes(q)),
    )
  }, [tickets, localQuery])

  if (!open) return null

  const listName = (id: string) => {
    const l = lists.find((l) => l.id === id)
    return l ? displayListName(l) : '—'
  }
  const queryActive = localQuery.trim().length > 0

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/30 px-4 py-12 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[82vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--color-border-soft)] px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]">
              <Inbox className="h-5 w-5" />
            </div>
            <div>
              <h3 className="flex items-center gap-2 text-[15px] font-semibold text-[var(--color-text-strong)]">
                Cartes archivées
                <span
                  className={cn(
                    'inline-flex min-w-[22px] items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-bold',
                    tickets.length > 0
                      ? 'bg-[var(--color-accent)] text-white'
                      : 'bg-[var(--color-surface-3)] text-[var(--color-text-dim)]',
                  )}
                >
                  {tickets.length}
                </span>
              </h3>
              <p className="mt-0.5 text-[11.5px] text-[var(--color-text-dim)]">
                {projectName}
                {queryActive &&
                  ` · ${filtered.length} résultat${
                    filtered.length > 1 ? 's' : ''
                  } pour « ${localQuery.trim()} »`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-1.5 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Filter */}
        {tickets.length > 0 && (
          <div className="shrink-0 border-b border-[var(--color-border-soft)] px-6 py-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--color-text-dim)]" />
              <input
                value={localQuery}
                onChange={(e) => setLocalQuery(e.target.value)}
                placeholder="Filtrer les archives…"
                className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] py-2 pl-9 pr-9 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
              />
              {queryActive && (
                <button
                  type="button"
                  onClick={() => setLocalQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
                  aria-label="Effacer le filtre"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {tickets.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-14 text-[var(--color-text-dim)]">
              <Inbox className="h-8 w-8" strokeWidth={1.6} />
              <span className="text-[13px]">
                Aucune carte archivée dans ce projet.
              </span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-14 text-[var(--color-text-dim)]">
              <Search className="h-8 w-8" strokeWidth={1.6} />
              <span className="text-[13px]">
                Aucune carte archivée ne correspond à « {localQuery.trim()} ».
              </span>
            </div>
          ) : (
            <ul className="space-y-2.5">
              {filtered.map((t) => (
                <ArchivedCard
                  key={t.id}
                  ticket={t}
                  listName={listName(t.list_id)}
                  expanded={expandedId === t.id}
                  onToggle={() =>
                    setExpandedId((id) => (id === t.id ? null : t.id))
                  }
                  onRestore={() => onRestore(t.id)}
                  onDelete={async () => {
                    const ok = await confirm({
                      title: `Supprimer définitivement « ${t.title} » ?`,
                      message: 'La carte archivée sera effacée pour de bon.',
                      confirmLabel: 'Supprimer',
                    })
                    if (ok) onDelete(t.id)
                  }}
                  onViewMedia={onViewMedia}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

/** A single archived card — collapsed summary that expands into a full preview. */
function ArchivedCard({
  ticket,
  listName,
  expanded,
  onToggle,
  onRestore,
  onDelete,
  onViewMedia,
}: {
  ticket: Ticket
  listName: string
  expanded: boolean
  onToggle: () => void
  onRestore: () => void
  onDelete: () => void
  onViewMedia: (urls: string[]) => void
}) {
  const media = groupMedia(ticket.media_urls)
  const messageCount = ticket.source_message_ids.length
  const hasMedia = ticket.media_urls.length > 0

  return (
    <li
      className={cn(
        'overflow-hidden rounded-2xl border bg-[var(--color-surface)] transition-colors',
        expanded
          ? 'border-[var(--color-accent)] ring-1 ring-inset ring-[var(--color-accent)]'
          : 'border-[var(--color-border-soft)] hover:border-[var(--color-border)]',
      )}
    >
      {/* Summary row — click to preview the card content */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start gap-3 px-4 py-3 text-left"
      >
        <span className="mt-0.5 shrink-0 text-[var(--color-text-dim)]">
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          {(ticket.priority || ticket.labels.length > 0) && (
            <div className="mb-1 flex flex-wrap items-center gap-1">
              {ticket.priority && (
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide ring-1 ring-inset',
                    PRIORITY_META[ticket.priority].chip,
                  )}
                >
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      PRIORITY_META[ticket.priority].dot,
                    )}
                  />
                  {PRIORITY_META[ticket.priority].label}
                </span>
              )}
              {ticket.labels.map((l) => (
                <span
                  key={l}
                  className={cn(
                    'rounded-md px-1.5 py-0.5 text-[9.5px] font-medium ring-1 ring-inset',
                    labelColor(l),
                  )}
                >
                  {l}
                </span>
              ))}
            </div>
          )}
          <div className="truncate text-[13px] font-medium text-[var(--color-text-strong)]">
            {ticket.title}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[var(--color-text-dim)]">
            <span>{listName}</span>
            <span>·</span>
            <span>{formatDate(ticket.source_date ?? ticket.created_at)}</span>
            {messageCount > 0 && (
              <span className="inline-flex items-center gap-0.5">
                <MessageSquare className="h-3 w-3" />
                {messageCount}
              </span>
            )}
            {hasMedia && (
              <span className="inline-flex items-center gap-0.5">
                <Paperclip className="h-3 w-3" />
                {ticket.media_urls.length}
              </span>
            )}
          </div>
        </div>
      </button>

      {/* Expanded preview */}
      {expanded && (
        <div className="border-t border-[var(--color-border-soft)] bg-[var(--color-surface-2)]/50 px-4 py-3">
          {ticket.description ? (
            <div
              className="rich-text text-[12.5px] leading-relaxed text-[var(--color-text)]"
              dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(ticket.description) }}
            />
          ) : (
            <div className="text-[12px] italic text-[var(--color-text-dim)]">
              Pas de description.
            </div>
          )}

          {hasMedia && (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              <MediaButton
                icon={<ImageIcon className="h-3.5 w-3.5" />}
                label="image"
                urls={media.image}
                onView={onViewMedia}
              />
              <MediaButton
                icon={<VideoIcon className="h-3.5 w-3.5" />}
                label="vidéo"
                urls={media.video}
                onView={onViewMedia}
              />
              <MediaButton
                icon={<Mic className="h-3.5 w-3.5" />}
                label="audio"
                urls={media.audio}
                onView={onViewMedia}
              />
              <MediaButton
                icon={<FileText className="h-3.5 w-3.5" />}
                label="PDF"
                urls={media.pdf}
                onView={onViewMedia}
              />
              <MediaButton
                icon={<Paperclip className="h-3.5 w-3.5" />}
                label="fichier"
                urls={media.file}
                onView={onViewMedia}
              />
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={onRestore}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-accent)] px-3 py-1.5 text-[12px] font-semibold text-white shadow-[var(--shadow-soft)] transition-colors hover:bg-[var(--color-accent-hover)]"
            >
              <ArchiveRestore className="h-3.5 w-3.5" />
              Restaurer la carte
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-[12px] font-medium text-[var(--color-text-dim)] transition-colors hover:border-[var(--color-danger)] hover:bg-[var(--color-danger-bg)] hover:text-[var(--color-danger)]"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Supprimer définitivement
            </button>
          </div>
        </div>
      )}
    </li>
  )
}

function MediaButton({
  icon,
  label,
  urls,
  onView,
}: {
  icon: ReactNode
  label: string
  urls: string[]
  onView: (urls: string[]) => void
}) {
  if (urls.length === 0) return null
  return (
    <button
      type="button"
      onClick={() => onView(urls)}
      className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border-soft)] bg-[var(--color-surface)] px-2 py-1 text-[11px] font-medium text-[var(--color-link)] transition-colors hover:border-[var(--color-link)]"
    >
      {icon}
      {urls.length} {label}
      {urls.length > 1 ? 's' : ''}
    </button>
  )
}
